'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Button } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import { cn } from '@core/utils';
import Image from 'next/image';
import { REWARD_UNIT_AMOUNT } from '@/app/donation/_utils/inviteReward';
import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getStaticCdnUrl } from '@/utils';
import { ATTRIBUTION_DAYS } from '../../_utils/labels';
const rewardRateOf = (unitAmount: number) => `${unitAmount * 100 / REWARD_UNIT_AMOUNT}%`;
const startStepsOf = (unitAmount: number | null) => [{
  title: '링크 만들기',
  description: '공유할 링크를 골라 링크를 만들어주세요.'
}, {
  title: '링크 복사하고 공유하기',
  description: '링크를 복사하여 SNS, 블로그, 메신저 등에 공유해보세요. 샘플을 복사해보세요.'
}, {
  title: '기부되면 수익받기',
  description: `추천 링크를 통해 들어온 유저가 ${ATTRIBUTION_DAYS}일 이내 기부하면 ${unitAmount === null ? '' : `${rewardRateOf(unitAmount)}의 `}수익을 받아요.`
}];
interface SectionTitleProps {
  title: string;
  isExample: boolean;
}
export const SectionTitle = t0 => {
  const $ = _c(5);
  const {
    title,
    isExample
  } = t0;
  let t1;
  if ($[0] !== isExample) {
    t1 = isExample ? <Badge dimensions="H20" className="bg-B-25 px-4 text-B-500">예시</Badge> : null;
    $[0] = isExample;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== title) {
    t2 = <h2 className="flex items-center gap-4 font-700 text-Gray-900 typo-body2">{title}{t1}</h2>;
    $[2] = t1;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
interface StartGuideCardProps {
  unitAmount: number | null;
  onShowExample: () => void;
}
export const StartGuideCard = t0 => {
  const $ = _c(10);
  const {
    unitAmount,
    onShowExample
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 className="font-700 text-Gray-900 typo-body2">이렇게 시작해요</h2>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onShowExample) {
    t2 = <div className="flex items-center justify-between gap-8">{t1}<Button theme="sub-line" dimensions="H32" onClick={onShowExample}>예시 보기</Button></div>;
    $[1] = onShowExample;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== unitAmount) {
    t3 = startStepsOf(unitAmount).map(_temp);
    $[3] = unitAmount;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <ol className="flex flex-col gap-8">{t3}</ol>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <section className="flex flex-col gap-12 break-keep rounded-small bg-[#f7fcff] p-20">{t2}{t4}</section>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
export const DemoIntro = t0 => {
  const $ = _c(6);
  const {
    unitAmount
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Image src={getStaticCdnUrl("partners/intro.png")} alt="" width={423} height={85} loading="eager" className="h-auto w-full max-w-[423px]" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col gap-8"><h2 className="font-700 text-Gray-900 typo-h6">링크를 공유하고, <br className="xl:hidden" />웰포인트를 받아보세요!</h2><p className="text-Gray-600 typo-body4">내가 SNS에 공유한 링크로 누군가 기부를 시작할 수 있어요.</p></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== unitAmount) {
    t3 = unitAmount === null ? null : <dl className="flex flex-col gap-4 rounded-small bg-Gray-white px-16 py-12 text-left"><dt className="font-500 text-Gray-600 typo-body4">웰로 파트너스 혜택</dt><dd className="font-600 text-Gray-900 typo-body4">내 링크로 기부가 발생하면, 기부금의 {rewardRateOf(unitAmount)}를 웰포인트로 받아요.</dd></dl>;
    $[2] = unitAmount;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <section className="flex flex-col items-center gap-12 break-keep bg-R-25 px-20 py-32 xl:gap-24 xl:rounded-large xl:p-32 xl:text-center">{t1}<div className="flex w-full flex-col gap-12">{t2}{t3}</div></section>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
interface DemoBannerProps {
  actionLabel: string;
  onAction: () => void;
}
export const DemoBanner = t0 => {
  const $ = _c(11);
  const {
    actionLabel,
    onAction
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  let t1;
  if ($[0] !== isDesktopViewport) {
    t1 = isDesktopViewport ? {
      top: DONATION_DESKTOP_STICKY_TOP
    } : undefined;
    $[0] = isDesktopViewport;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="rounded-xxsmall bg-Gray-white px-8 py-4 font-700 text-B-500 typo-caption1">DEMO</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="flex min-w-0 items-center gap-8 text-Gray-white">{t2}<span className="font-600 typo-body4 xl:font-700 xl:typo-body3"><span className="xl:hidden">예시 데이터로 보고 있어요</span><span className="max-xl:hidden">예시 모드로 보고 있어요</span></span><span className="opacity-85 typo-body4 max-xl:hidden">· 화면의 링크와 실적은 모두 예시 데이터예요</span></p>;
    t4 = cn(FOCUS_RING, "h-24 shrink-0 rounded-xxsmall border border-Gray-white px-8 font-600 text-Gray-white transition-opacity typo-caption2 hover:opacity-80 active:opacity-70", "xl:h-40 xl:rounded-xsmall xl:bg-Gray-white xl:px-16 xl:font-700 xl:text-Gray-black xl:typo-body4");
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== actionLabel || $[6] !== onAction) {
    t5 = <div className="mx-auto flex w-full max-w-screen-xl items-center justify-between gap-12 px-20 py-12">{t3}<button className={t4} onClick={onAction}>{actionLabel}</button></div>;
    $[5] = actionLabel;
    $[6] = onAction;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t5) {
    t6 = <div className="sticky z-10 bg-B-500 mobile-tablet:top-48" style={t1}>{t5}</div>;
    $[8] = t1;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
function _temp(step, index) {
  return <li key={step.title} className="flex gap-12 rounded-small border border-Gray-50 bg-Gray-white px-16 py-12"><span className="flex size-28 shrink-0 items-center justify-center rounded-small bg-B-550 font-700 text-Gray-white typo-body4">{index + 1}</span><span className="flex flex-col gap-8"><span className="font-700 text-Gray-900 typo-body3">{step.title}</span><span className="text-Gray-700 typo-body4">{step.description}</span></span></li>;
}